<template>
  <Dialog
    title="Factory Nesting Settings"
    :show-close-button="true"
    :show-footer="true"
    :show-save-button="true"
    :show-cancel-button="true"
    :save-button-disabled="saving || loading"
    :loading="loading"
    sm
    @closeEvent="emit('close')"
    @cancelEvent="emit('close')"
    @saveEvent="save"
  >
    <template #modal-content>
      <div class="flex flex-col gap-4">
        <p class="text-sm text-gray-600">
          Paper the nesting packer lays panels onto for
          <span class="font-semibold">{{ factoryName }}</span
          >. Dimensions in inches; each full sheet becomes one nested SVG.
        </p>

        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Paper width (in)</span>
          <input
            v-model.number="form.paper_width"
            type="number"
            min="4"
            max="400"
            step="1"
            class="border rounded-md px-2 py-1.5"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Paper length (in)</span>
          <input
            v-model.number="form.paper_length"
            type="number"
            min="4"
            max="4000"
            step="1"
            class="border rounded-md px-2 py-1.5"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Piece padding (in)</span>
          <input
            v-model.number="piecePadding"
            type="number"
            min="0"
            max="8"
            step="0.0625"
            class="border rounded-md px-2 py-1.5"
          />
          <span class="text-xs text-gray-500">
            Clear paper kept between pieces, measured from the outermost cutting outline (default
            0.125)
          </span>
        </label>

        <fieldset class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Allowed panel rotation</span>
          <label class="flex items-center gap-2">
            <input v-model="rotate90" type="checkbox" />
            <span>90° — packer may turn panels sideways for a tighter fit</span>
          </label>
          <label class="flex items-center gap-2">
            <input v-model="rotate180" type="checkbox" />
            <span>180° — allowed for directional fabric (does not change packing)</span>
          </label>
        </fieldset>

        <fieldset class="flex flex-col gap-2 text-sm">
          <span class="font-medium">Laser cutting outlines</span>
          <label class="flex flex-col gap-1">
            <span>Cut outline width (mm)</span>
            <input
              v-model.number="cutWidthMm"
              type="number"
              min="0.05"
              max="10"
              step="0.05"
              class="border rounded-md px-2 py-1.5"
            />
            <span class="text-xs text-gray-500">
              Black band going outwards from the panel edge (never over the artwork). E.g. 1.41 (= 4
              pt) or 0.25.
            </span>
          </label>
          <label class="flex items-center gap-2">
            <input v-model="searchOutline" type="checkbox" />
            <span>Searching outline — extra line offset outwards for the cutter to find</span>
          </label>
          <div v-if="searchOutline" class="grid grid-cols-2 gap-3 pl-6">
            <label class="flex flex-col gap-1">
              <span>Outwards offset (mm)</span>
              <input
                v-model.number="searchOffsetMm"
                type="number"
                min="1"
                max="100"
                step="0.5"
                class="border rounded-md px-2 py-1.5"
              />
            </label>
            <label class="flex flex-col gap-1">
              <span>Line thickness (mm)</span>
              <input
                v-model.number="searchWidthMm"
                type="number"
                min="0.1"
                max="50"
                step="0.5"
                class="border rounded-md px-2 py-1.5"
              />
            </label>
          </div>
        </fieldset>

        <fieldset class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Piece labels</span>
          <p class="text-xs text-gray-500">
            The size is always printed at each panel's bottom edge. Extras print above it in a
            smaller font:
          </p>
          <label class="flex items-center gap-2">
            <input v-model="labelOrderNo" type="checkbox" />
            <span>Order number</span>
          </label>
          <label class="flex items-center gap-2">
            <input v-model="labelPlayer" type="checkbox" />
            <span>Player name / number</span>
          </label>
          <label class="flex items-center gap-2">
            <input v-model="labelPanel" type="checkbox" />
            <span>Panel name</span>
          </label>
        </fieldset>

        <p v-if="errorText" class="text-sm text-red-600">{{ errorText }}</p>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import Dialog from '@/components/Shared/Dialog.vue';
import { showToast } from '@/composables/swal';
import { handleResponseException } from '@/interceptors/axiosRequestInterceptor';
import { useFactorySettings } from '@/composables/useFactorySettings';
import type { FactoryNestingSettings } from '@/models/FactoryNestingSettings';

const props = defineProps<{
  factoryId: number;
  factoryName?: string | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'saved', value: FactoryNestingSettings): void;
}>();

const { getNestingSettings, saveNestingSettings } = useFactorySettings();

const loading = ref(true);
const saving = ref(false);
const errorText = ref('');
const rotate90 = ref(true);
const rotate180 = ref(false);
const piecePadding = ref(0.125);
const cutWidthMm = ref(1);
const searchOutline = ref(false);
const searchOffsetMm = ref(5);
const searchWidthMm = ref(1);
const labelOrderNo = ref(false);
const labelPlayer = ref(false);
const labelPanel = ref(false);

const form = reactive<FactoryNestingSettings>({
  paper_width: 63,
  paper_length: 118,
  allowed_rotations: [],
});

const factoryName = computed(() => props.factoryName || `factory #${props.factoryId}`);

onMounted(async () => {
  try {
    const existing = await getNestingSettings(props.factoryId);
    if (existing) {
      form.paper_width = existing.paper_width;
      form.paper_length = existing.paper_length;
      rotate90.value = existing.allowed_rotations?.includes(90) ?? false;
      rotate180.value = existing.allowed_rotations?.includes(180) ?? false;
      if (existing.piece_padding != null) piecePadding.value = existing.piece_padding;
      if (existing.cut_outline_width_mm != null) cutWidthMm.value = existing.cut_outline_width_mm;
      searchOutline.value = !!existing.search_outline;
      if (existing.search_outline_offset_mm != null) {
        searchOffsetMm.value = existing.search_outline_offset_mm;
      }
      if (existing.search_outline_width_mm != null) {
        searchWidthMm.value = existing.search_outline_width_mm;
      }
      labelOrderNo.value = !!existing.label_order_no;
      labelPlayer.value = !!existing.label_player;
      labelPanel.value = !!existing.label_panel;
    }
  } catch (error) {
    handleResponseException(error);
  } finally {
    loading.value = false;
  }
});

async function save(): Promise<void> {
  errorText.value = '';
  if (!(form.paper_width >= 4 && form.paper_width <= 400)) {
    errorText.value = 'Paper width must be between 4 and 400 in.';
    return;
  }
  if (!(form.paper_length >= 4 && form.paper_length <= 4000)) {
    errorText.value = 'Paper length must be between 4 and 4000 in.';
    return;
  }
  if (!(piecePadding.value >= 0 && piecePadding.value <= 8)) {
    errorText.value = 'Piece padding must be between 0 and 8 in.';
    return;
  }
  if (!(cutWidthMm.value >= 0.05 && cutWidthMm.value <= 10)) {
    errorText.value = 'Cut outline width must be between 0.05 and 10 mm.';
    return;
  }
  if (searchOutline.value) {
    if (!(searchOffsetMm.value >= 1 && searchOffsetMm.value <= 100)) {
      errorText.value = 'Searching outline offset must be between 1 and 100 mm.';
      return;
    }
    if (!(searchWidthMm.value >= 0.1 && searchWidthMm.value <= 50)) {
      errorText.value = 'Searching outline thickness must be between 0.1 and 50 mm.';
      return;
    }
    if (searchWidthMm.value / 2 >= searchOffsetMm.value) {
      errorText.value = 'The searching line must sit clear of the panel (thickness/2 < offset).';
      return;
    }
  }

  saving.value = true;
  try {
    const value: FactoryNestingSettings = {
      paper_width: form.paper_width,
      paper_length: form.paper_length,
      allowed_rotations: [...(rotate90.value ? [90] : []), ...(rotate180.value ? [180] : [])],
      piece_padding: piecePadding.value,
      cut_outline_width_mm: cutWidthMm.value,
      search_outline: searchOutline.value,
      search_outline_offset_mm: searchOffsetMm.value,
      search_outline_width_mm: searchWidthMm.value,
      label_order_no: labelOrderNo.value,
      label_player: labelPlayer.value,
      label_panel: labelPanel.value,
    };
    const saved = await saveNestingSettings(props.factoryId, value);
    showToast('Nesting settings saved', 'success');
    emit('saved', saved ?? value);
    emit('close');
  } catch (error) {
    handleResponseException(error);
  } finally {
    saving.value = false;
  }
}
</script>
